import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { 
  History, Search, Filter, User, 
  Clock, Monitor, MousePointer2, Calendar,
  RefreshCcw, Activity, Users
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Auditoría de Sistema (Logs)
 * Refactorizado al estilo Productos / ConfigFacturacion
 */
const Logs = () => {
  const { profile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <span className="opacity-0 group-hover:opacity-30 inline-block ml-1">↕</span>;
    }
    return sortConfig.direction === 'ascending' ? <span className="text-[#51A7FD] inline-block ml-1">↑</span> : <span className="text-[#51A7FD] inline-block ml-1">↓</span>;
  };

  useEffect(() => {
    if (profile?.organization_id) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // Traemos los logs uniendo con la información del perfil del usuario
      const { data, error } = await supabase
        .from('usage_logs')
        .select(`
          *,
          profiles:user_id (full_name, email)
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      toast.error('Error al cargar historial de auditoría');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let result = logs.filter(log => 
      (log.profiles?.full_name || '').toLowerCase().includes(term) ||
      (log.module_id || '').toLowerCase().includes(term) ||
      (log.button_clicked || '').toLowerCase().includes(term)
    );

    if (sortConfig !== null) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'profiles.full_name') {
           aValue = a.profiles?.full_name || '';
           bValue = b.profiles?.full_name || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [logs, searchTerm, sortConfig]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = filteredLogs.length;
    const uniqueUsers = new Set(filteredLogs.map(l => l.user_id)).size;
    
    // Average time spent (only positive values)
    const timeLogs = filteredLogs.filter(l => (l.time_spent_seconds || 0) > 0);
    const avgTime = timeLogs.length > 0 
        ? timeLogs.reduce((acc, curr) => acc + curr.time_spent_seconds, 0) / timeLogs.length 
        : 0;

    return { total, uniqueUsers, avgTime: Math.round(avgTime) };
  }, [filteredLogs]);

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && logs.length === 0) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
      <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Cargando Auditoría...</p>
    </div>
  );

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <History size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Auditoría de Sistema</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Registro de actividad y trazabilidad de usuarios.
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <button
            onClick={fetchLogs}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} /> Refrescar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Eventos Registrados</p>
            <Activity size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Acciones recientes</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Usuarios Activos</p>
            <Users size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.uniqueUsers}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">En este lote</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tiempo Promedio</p>
            <Clock size={16} className="text-purple-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.avgTime}s</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Estadía por acción</p>
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4">
         <div className="relative">
             <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                <Search size={10} /> Buscar Evento
             </label>
             <div className="relative">
                <input 
                    type="text"
                    placeholder="USUARIO, MÓDULO O ACCIÓN..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase text-slate-700"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
             </div>
         </div>
      </div>

      {/* TABLA DE LOGS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto imp-table-wrap">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-6 py-4 pl-6 cursor-pointer hover:bg-slate-100 transition-colors group select-none" onClick={() => requestSort('created_at')}>
                  Fecha y Hora {getSortIcon('created_at')}
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors group select-none" onClick={() => requestSort('profiles.full_name')}>
                  Usuario / Rol {getSortIcon('profiles.full_name')}
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors group select-none" onClick={() => requestSort('module_id')}>
                  Módulo {getSortIcon('module_id')}
                </th>
                <th className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors group select-none" onClick={() => requestSort('button_clicked')}>
                  Acción / Botón {getSortIcon('button_clicked')}
                </th>
                <th className="px-6 py-4 text-right pr-6 cursor-pointer hover:bg-slate-100 transition-colors group select-none" onClick={() => requestSort('time_spent_seconds')}>
                  Estadía {getSortIcon('time_spent_seconds')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                    <RefreshCcw className="animate-spin mx-auto mb-2" size={20} />
                    Cargando historial...
                  </td>
                </tr>
              ) : filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-blue-50/20 transition-colors group">
                  <td className="px-6 py-4 pl-6 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-[10px] font-bold text-slate-600">
                      <Calendar size={14} className="text-slate-300" />
                      {formatFecha(log.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200 shadow-sm group-hover:border-blue-200 transition-colors">
                        <User size={14} />
                      </div>
                      <div>
                        <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight leading-none mb-1 group-hover:text-[#51A7FD] transition-colors">
                          {log.profiles?.full_name || 'Desconocido'}
                        </p>
                        <span className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                          {log.user_role || 'S/R'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Monitor size={14} className="text-blue-400" />
                      <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">{log.module_id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MousePointer2 size={14} className="text-emerald-400" />
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                        {log.button_clicked || 'Navegación'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 pr-6 text-right">
                    <div className="flex items-center justify-end gap-2 text-[10px] font-black text-slate-400">
                      <Clock size={14} />
                      {log.time_spent_seconds}s
                    </div>
                  </td>
                </tr>
              ))}

              {filteredLogs.length === 0 && !loading && (
                <tr>
                  <td colSpan="5" className="py-16 text-center opacity-50 flex flex-col items-center">
                    <History size={40} className="text-slate-300 mb-2" strokeWidth={1} />
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      No se encontraron registros
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
};

export default Logs;